import { useState } from "react";
import { Link } from "react-router-dom";

import { nvAgentDetailPath } from "@/components/navigator";
import { safeHttpUrl } from "@/lib/safe-url";
import { cn } from "@/lib/utils";
import type { DetailSupplier } from "@/services/navigator/agent-detail-service";
import { recordEvent } from "@/services/navigator/finder-service";

import { NvShieldCheckIcon } from "./detail-icons";
import { DetailLabel, DetailSection } from "./detail-parts";
import { SupplierLogo } from "./supplier-logo";

// 공급사 정보 (설계서 slide 49 · 51 · 52 · Figma 03-01~03/05): 회사 카드(로고 + 인증 보유 표식 · 회사 유형 · 이름 · 홈페이지 · 규모 지표 3종 · 기타 회사 자산)
// + 이 공급사의 다른 제품(5개를 넘으면 4개 + 더보기). 인증 보유 표식은 공급자에게 표기하지 않고(서버가 cert_badge로 결정),
// 회사 정보 띠 · 대표 고객사는 보라 '내부 전용' 태그와 함께 PwC 내부에게만(서버가 internal을 줄 때만).

const FOLD_AT = 5;
const FOLDED_COUNT = 4;

function InternalTag() {
  return <span className="inline-flex h-[15px] items-center rounded-nv-pill bg-nv-purple-subtle px-[6px] text-[11px] font-bold leading-[13px] text-nv-purple">내부 전용</span>;
}

function Metric({ label, value, small }: { label: string; value: string; small?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-[6px] border-nv-border px-0 md:border-l md:px-6 md:first:border-l-0 md:first:pl-0">
      <span className="text-[14px] leading-[22px] text-nv-text-secondary">{label}</span>
      <span className={cn("break-keep text-nv-text-primary", small ? "text-[20px] font-medium leading-[25px]" : "text-[26px] leading-[33px]")}>{value}</span>
    </div>
  );
}

function OtherProducts({ supplier }: { supplier: DetailSupplier }) {
  const [expanded, setExpanded] = useState(false);
  const items = supplier.other_products;
  const folded = items.length > FOLD_AT && !expanded;
  const shown = folded ? items.slice(0, FOLDED_COUNT) : items;
  return (
    <div className="flex flex-col gap-5 border-t border-nv-border pt-8">
      <h3 className="text-[16px] leading-[26px] text-nv-text-primary">이 공급사의 다른 제품 {supplier.other_products_total}</h3>
      <div className="grid gap-4 md:grid-cols-2">
        {shown.map((p) => (
          <Link
            key={p.id}
            to={nvAgentDetailPath(p.id)}
            state={{ source: "detail" }}
            className="flex min-h-[144px] flex-col gap-2 rounded-nv-card border border-nv-border bg-nv-surface px-[18px] py-[18px] hover:border-nv-border-strong"
          >
            <span className="text-[15px] leading-6 text-nv-text-primary">{p.name}</span>
            {p.summary && <span className="line-clamp-3 text-[14px] leading-[22px] text-nv-text-secondary">{p.summary}</span>}
            {p.agent_type && <span className="mt-auto inline-flex h-[21px] self-start items-center rounded-nv-pill bg-[#EDEDEA] px-2 text-[12px] leading-[19px] text-nv-text-secondary">{p.agent_type}</span>}
          </Link>
        ))}
      </div>
      {folded && (
        <button type="button" onClick={() => setExpanded(true)} className="self-center text-[14px] leading-[22px] text-nv-text-secondary underline-offset-2 hover:underline">
          더보기 ({items.length - FOLDED_COUNT})
        </button>
      )}
    </div>
  );
}

export function SupplierSection({ supplier, agentId }: { supplier: DetailSupplier; agentId: string }) {
  const homepage = safeHttpUrl(supplier.homepage, true);
  const metrics = [
    { label: "임직원 규모", value: supplier.company_size },
    { label: "누적 도입 고객사", value: supplier.customer_scale },
    { label: "소재지", value: supplier.location, small: true },
  ].filter((m): m is { label: string; value: string; small?: boolean } => !!m.value);
  const internal = supplier.internal;
  const hasBand = !!internal && (internal.certifications.length > 0 || !!internal.funding_stage || !!internal.revenue_range);
  return (
    <DetailSection id="supplier" title="공급사 정보">
      <div className="flex flex-col gap-8 rounded-[20px] border border-nv-border bg-nv-surface p-6 md:p-10">
        <div className="flex flex-col gap-6 md:flex-row md:gap-8">
          <div className="flex h-[147px] w-[120px] shrink-0 flex-col items-center justify-center gap-[10px] self-start rounded-[14px] border border-nv-border bg-nv-surface shadow-nv-card">
            <SupplierLogo name={supplier.name} logoUrl={supplier.logo_url} size={92} />
            {supplier.cert_badge && (
              <span className="inline-flex items-center gap-1 text-[12px] leading-[19px] text-nv-text-primary">
                <NvShieldCheckIcon size={14} />
                인증 보유
              </span>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-[6px]">
            {supplier.company_type && (
              <span className="inline-flex h-[27px] self-start items-center rounded-nv-chip bg-[linear-gradient(90deg,#FDEBDD,#F1E6F5)] px-[10px] text-[13px] leading-[21px] text-nv-text-primary">{supplier.company_type}</span>
            )}
            <h3 className="text-[24px] font-normal leading-[38px] text-nv-text-primary">{supplier.name}</h3>
            {homepage && (
              <a
                href={homepage}
                target="_blank"
                rel="noreferrer"
                onClick={() => recordEvent("homepage_click", agentId)}
                className="self-start text-[14px] leading-[22px] text-nv-text-secondary hover:text-nv-text-primary hover:underline"
              >
                {supplier.homepage_label ?? supplier.homepage}
              </a>
            )}
            {metrics.length > 0 && (
              <div className="mt-5 grid gap-4 md:grid-cols-3 md:gap-0 xl:grid-cols-[repeat(3,290px)]">
                {metrics.map((m) => (
                  <Metric key={m.label} {...m} />
                ))}
              </div>
            )}
          </div>
        </div>

        {hasBand && internal && (
          <div className="-mb-0 mt-2 flex flex-wrap items-center justify-center gap-x-[43px] gap-y-2 rounded-nv-control bg-nv-bg-page px-5 py-[14px] text-[14px] leading-[22px]">
            {internal.certifications.length > 0 && (
              <span className="text-nv-text-primary">
                <span className="text-[12px] text-black">보유 인증</span> {internal.certifications.join(", ")}
              </span>
            )}
            {internal.funding_stage && (
              <span className="flex items-center gap-2">
                <span className="text-nv-text-secondary">자금 조달</span>
                <span className="text-nv-text-primary">{internal.funding_stage}</span>
                <InternalTag />
              </span>
            )}
            {internal.revenue_range && (
              <span className="flex items-center gap-2">
                <span className="text-nv-text-secondary">매출 규모</span>
                <span className="text-nv-text-primary">{internal.revenue_range}</span>
                <InternalTag />
              </span>
            )}
          </div>
        )}

        {internal && internal.reference_customers.length > 0 && (
          <div className="-mt-[6px] flex flex-col gap-2 md:flex-row md:gap-6">
            <span className="flex w-[164px] shrink-0 items-start gap-[6px] pt-[2px]">
              <DetailLabel>대표 고객사</DetailLabel>
              <InternalTag />
            </span>
            <div className="flex flex-wrap gap-x-[14px] gap-y-1">
              {internal.reference_customers.map((r, i) => (
                <span key={i} className="break-keep text-[15px] leading-6">
                  {r.industry && <span className="mr-1 text-nv-text-tertiary">[{r.industry}]</span>}
                  <span className="text-nv-text-primary">{r.customers}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {supplier.assets.length > 0 && (
          <div className={cn("flex flex-col gap-2 md:flex-row md:gap-6", internal ? "-mt-[11px]" : "mt-3")}>
            <DetailLabel className="w-[158px] shrink-0 pt-[2px]">기타 회사 자산</DetailLabel>
            <ul className="flex min-w-0 flex-1 flex-col gap-[2px]">
              {supplier.assets.map((a, i) => (
                <li key={i} className="text-[14px] leading-[22px] text-nv-text-secondary">
                  {a}
                </li>
              ))}
            </ul>
          </div>
        )}

        {supplier.other_products.length > 0 && <OtherProducts supplier={supplier} />}
      </div>
    </DetailSection>
  );
}
